import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Home, 
  Smartphone, 
  Settings, 
  ArrowRight, 
  CheckCircle, 
  Star,
  Lightbulb,
  Music,
  Shield,
  Zap 
} from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";
import LoxoneLogo from '/lovable-uploads/61ca03ab-7d73-4d3e-858b-4957ab9d692a.png';

const smartHomeFeatures = [
  {
    icon: Lightbulb,
    title: "Intelligente Verlichting",
    description: "Automatische bediening op basis van aanwezigheid"
  },
  {
    icon: Music,
    title: "Multiroom Audio",
    description: "Muziek door het hele huis gesynchroniseerd"
  },
  {
    icon: Shield,
    title: "Beveiliging & Toegang",
    description: "Complete controle over toegang en veiligheid"
  },
  {
    icon: Zap,
    title: "Energiebeheer",
    description: "Optimaal gebruik van zonnepanelen & batterijen"
  }
];

export function LoxoneSmartHomeSection() {
  return (
    <section className="py-16 bg-gradient-to-br from-muted/30 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <img src={LoxoneLogo} alt="Loxone" className="h-5 w-5" />
              <Star className="h-4 w-4" />
               Loxone Partner
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete <span className="text-primary">Smart Home</span> Oplossingen
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Eén systeem dat alle merken en technologieën in uw woning intelligent verbindt. 
              Van verlichting tot audio, van klimaat tot energiebeheer.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            {/* Left: Video & Benefits */}
            <div>
              <Card className="overflow-hidden mb-6">
                <div className="relative aspect-video bg-black">
                  <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    className="w-full h-full object-cover"
                  >
                    <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Basics%20%26%20Accessories/100335%20-%20Miniserver/Images/vi-miniserver-overview-100335-miniserver.webm" type="video/webm" />
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      <div className="text-center">
                        <Settings className="h-12 w-12 mx-auto mb-2" />
                        <p>Loxone Miniserver - Het hart van uw Smart Home</p>
                      </div>
                    </div>
                  </video>
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                    <span className="text-sm font-medium text-primary">Loxone Miniserver</span>
                  </div>
                </div>
              </Card>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm md:text-base">Eén systeem voor alle smart home functies</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm md:text-base">Compatible met 500+ merken (Philips, Sonos, Apple)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm md:text-base">Volledig geautomatiseerd - denkt voor u mee</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm md:text-base">Professionele installatie & ondersteuning</span>
                </div>
              </div>
            </div>

            {/* Right: Features Grid */}
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {smartHomeFeatures.map((feature, index) => (
                  <Card key={index} className="p-4 hover:shadow-md transition-shadow">
                    <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                      <feature.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h4 className="font-semibold text-sm mb-2">{feature.title}</h4>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </Card>
                ))}
              </div>

              {/* Key Benefits */}
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-6 rounded-xl">
                <h3 className="font-bold text-lg mb-4 text-center">Waarom Loxone?</h3>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary">500+</div>
                    <div className="text-xs text-muted-foreground">Compatible merken</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">30%</div>
                    <div className="text-xs text-muted-foreground">Energiebesparing</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">24/7</div>
                    <div className="text-xs text-muted-foreground">Automatische werking</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">1</div>
                    <div className="text-xs text-muted-foreground">App voor alles</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                variant="hero"
                onClick={() => {
                  track("loxone_section_calendly");
                  (window as any).Calendly?.initPopupWidget?.({
                    url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                    pageSettings: {
                      backgroundColor: 'ffffff',
                      hideEventTypeDetails: false,
                      hideLandingPageDetails: false,
                      primaryColor: 'ff7800',
                      textColor: '4d5055'
                    }
                  });
                }}
              >
                Plan Smart Home Advies
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link 
                  to="/smarthome"
                  onClick={() => track("loxone_section_more_info")}
                >
                  <Home className="mr-2 h-4 w-4" />
                  Meer over Loxone
                </Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Als Loxone Partner garanderen wij professionele installatie en support
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}